import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { LuVolume2, LuVolumeX } from "react-icons/lu";
import { setSound, soundOn, subscribeSound } from "../sound";
import { calm } from "./fx";

/**
 * "Juice": the small, friendly things that make a game feel alive – a speaker switch, floating "+1"s and big centre banners ("Din tur!",
 * "5 i træk!"). Everything here is decoration: the same information is always also written in the normal, readable interface, so
 * nothing is lost for screen readers, "less motion" or when the sound is off.
 */

export type PopTone = "good" | "gold" | "blue" | "pink" | "soft";
interface FxItem { id: number; kind: "pop" | "banner"; text: string; tone: PopTone; dx: number }

const subs = new Set<(i: FxItem) => void>();
let seq = 1;
const emit = (kind: FxItem["kind"], text: string, tone: PopTone) => {
  const item: FxItem = { id: seq++, kind, text, tone, dx: ((seq * 37) % 61) - 30 };
  subs.forEach((s) => s(item));
};
/** a small floating label that drifts up and fades ("+10", "Træffer!") */
export const pop = (text: string, tone: PopTone = "good") => emit("pop", text, tone);
/** a big announcement across the middle of the screen ("Din tur!") – only one at a time */
export const banner = (text: string, tone: PopTone = "blue") => emit("banner", text, tone);

/** Renders the pops and banners. One instance per game screen; it never takes the focus or blocks a click. */
export function FxLayer() {
  const [items, setItems] = useState<FxItem[]>([]);
  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const on = (i: FxItem) => {
      setItems((cur) => [...cur.filter((c) => (i.kind === "banner" ? c.kind !== "banner" : true)).slice(-5), i]);
      const t = setTimeout(() => { timers.delete(t); setItems((cur) => cur.filter((c) => c.id !== i.id)); }, i.kind === "banner" ? 1500 : 1300);
      timers.add(t);
    };
    subs.add(on);
    return () => { subs.delete(on); timers.forEach(clearTimeout); };
  }, []);
  if (!items.length) return null;
  return (
    <div className="fxlayer" aria-hidden="true">
      {items.map((i) => i.kind === "banner"
        ? <div key={i.id} className={`fx-banner ${i.tone}`}><span>{i.text}</span></div>
        : <div key={i.id} className={`fx-pop ${i.tone}`} style={{ ["--dx" as any]: `${i.dx}px` }}>{i.text}</div>)}
    </div>
  );
}

/** The speaker switch shown above every quick game. */
export function SoundToggle() {
  const on = useSyncExternalStore(subscribeSound, soundOn, () => true);
  return (
    <button type="button" className="btn ghost small snd" aria-pressed={on} aria-label={on ? "Lyd: tændt. Tryk for at slå lyden fra" : "Lyd: slået fra. Tryk for at slå lyden til"} onClick={() => setSound(!on)}>
      {on ? <LuVolume2 size={16} aria-hidden="true" /> : <LuVolumeX size={16} aria-hidden="true" />} Lyd
    </button>
  );
}

/** v22: the speaker in the top bar – one switch for the sound of the whole website (buttons, games, the world) */
export function SoundSwitch({ className = "" }: { className?: string }) {
  const on = useSyncExternalStore(subscribeSound, soundOn, () => true);
  return (
    <button type="button" className={`snd-switch${on ? "" : " off"} ${className}`.trim()} aria-pressed={on} aria-label={on ? "Lyd: tændt. Tryk for at slå lyden fra" : "Lyd: slået fra. Tryk for at slå lyden til"} title={on ? "Lyd tændt" : "Lyd slået fra"} data-sfx={on ? "off" : "on"} onClick={() => setSound(!on)}>
      {on ? <LuVolume2 size={20} aria-hidden="true" /> : <LuVolumeX size={20} aria-hidden="true" />}
    </button>
  );
}

/** The value from the previous render (undefined on the first one). */
export function usePrevious<T>(v: T): T | undefined {
  const r = useRef<T | undefined>(undefined);
  const prev = r.current;
  useEffect(() => { r.current = v; });
  return prev;
}

/**
 * Calls `fn(now, before)` whenever `value` changes – but never for the very first value (opening a game in the middle of a match
 * must not make a noise). `key` makes it re-arm when the match changes.
 */
export function useOnChange<T>(value: T, fn: (now: T, before: T) => void, key: unknown = "") {
  const last = useRef<{ k: unknown; v: T } | null>(null);
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    const l = last.current;
    if (l && l.k === key && !Object.is(l.v, value)) cb.current(value, l.v);
    last.current = { k: key, v: value };
  }, [value, key]);
}

/** true for ~`ms` after `value` changed – handy for a short CSS class ("this just happened"). */
export function useRecent(value: unknown, ms = 900): boolean {
  const [on, setOn] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setOn(true);
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return on;
}

/** A number that counts up (or down) to `value` instead of jumping – for scores. With "less motion" it jumps. */
export function useTween(value: number, ms = 600): number {
  const [v, setV] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (calm() || value === from.current) { from.current = value; setV(value); return; }
    const a = from.current, t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / ms), e = 1 - Math.pow(1 - p, 3);
      const cur = Math.round(a + (value - a) * e);
      from.current = cur;
      setV(cur);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return v;
}

/**
 * Calls `fn(entry)` once for every NEW entry of a game's event log (the server keeps the last ~20 events). Entries that were already there
 * when the match was opened (or when `key` changed) are skipped, so re-opening a game in the middle of a match stays silent.
 */
export interface LogItem { id: number; kind: string; seat?: number; data?: Record<string, any> }
export function useLogEvents(log: LogItem[] | undefined, key: string, fn: (e: LogItem) => void) {
  const last = useRef<{ key: string; id: number } | null>(null);
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    const items = log ?? [];
    const maxId = items.length ? items[items.length - 1]!.id : 0;
    const l = last.current;
    if (!l || l.key !== key) { last.current = { key, id: maxId }; return; }
    for (const e of items) if (e.id > l.id) cb.current(e);
    l.id = Math.max(l.id, maxId);
  }, [log, key]);
}
